import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { getPrintMailQuote, queueMailPiece, verifyPrintMailAddress } from 'services'
import { Modal } from 'stories/components'

import { PrintMailStatusBanner } from './StatusBanner'
import { IMailPostageOverrides, mailMoney, mailPurposeLabel } from './types'
import { usePrintMailOffer } from './usePrintMailOffer'

const extraServiceOptions: Record<string, string> = {
  certified: 'Certified',
  certified_return_receipt: 'Certified with return receipt',
  registered: 'Registered',
}

interface Quote {
  estimatedCostCents: number
  postage: Record<string, any>
  pageCount: number
}

/**
 * Post one document the platform already holds.
 *
 * Queued, not sent: this writes a row and returns, and the dispatcher submits it to the provider
 * afterwards - so pressing the button has spent nothing yet and the piece can still be cancelled
 * for free from the Print & Mail queue.
 *
 * Only the purposes a tenant has switched on are offered. That is what keeps the service general
 * without making it indiscriminate: a company that posts statements and late notices has said so,
 * and nothing else can be put in an envelope on their account by accident.
 */
export const MailDocumentModal = ({
  loanNumber,
  documentKey,
  documentName,
  onClose,
  onQueued,
}: {
  loanNumber: number
  documentKey: string
  documentName: string
  onClose: () => void
  onQueued?: () => void
}) => {
  const { config, status, purposes, canOffer } = usePrintMailOffer(true)

  const [purpose, setPurpose] = useState('')
  const [pageCount, setPageCount] = useState('1')
  const [extraService, setExtraService] = useState<string>('__default')
  const [quote, setQuote] = useState<Quote | null>(null)
  const [quoting, setQuoting] = useState(false)
  const [sending, setSending] = useState(false)
  const [address, setAddress] = useState<{ checked: boolean; text: string; warning: string }>({
    checked: false,
    text: '',
    warning: '',
  })

  useEffect(() => {
    if (!purpose && purposes.length) setPurpose(purposes[0])
  }, [purposes])

  const overrides = useMemo<IMailPostageOverrides | undefined>(
    () => (extraService === '__default' ? undefined : { extraService }),
    [extraService],
  )

  /** The quote is re-read whenever anything that changes the price changes. */
  useEffect(() => {
    if (!purpose) return
    let cancelled = false
    setQuoting(true)
    getPrintMailQuote({ purpose, pageCount: Math.max(Number(pageCount) || 1, 1), overrides })
      .then((res: any) => {
        if (cancelled) return
        setQuote({
          estimatedCostCents: res?.estimatedCostCents || 0,
          postage: res?.postage || {},
          pageCount: res?.pageCount || 1,
        })
      })
      .catch((err: any) => {
        if (!cancelled) toast(err?.message || 'Could not price this letter.', { type: 'error' })
      })
      .finally(() => !cancelled && setQuoting(false))
    return () => {
      cancelled = true
    }
  }, [purpose, pageCount, overrides])

  /**
   * Where it would actually go.
   *
   * Worth a round trip before anyone commits: the alternative is finding out from returned mail,
   * and an address the carrier will not vouch for is held rather than posted anyway.
   */
  useEffect(() => {
    if (!loanNumber || !status?.configured) return
    let cancelled = false
    verifyPrintMailAddress({ loanNumber })
      .then((res: any) => {
        if (cancelled) return
        if (!res?.found) {
          setAddress({ checked: true, text: '', warning: res?.reason || 'No mailing address on file for the borrower' })
          return
        }
        const parsed = res.verified?.address || res.parsed || {}
        const text = [
          parsed.addressLine1,
          parsed.addressLine2,
          [parsed.city, [parsed.state, parsed.zip].filter(Boolean).join(' ')].filter(Boolean).join(', '),
        ]
          .filter(Boolean)
          .join(', ')
        const verified = res.verified
        const warning = res.reason
          ? res.reason
          : verified && !verified.deliverable
          ? verified.missingUnit
            ? 'The carrier reaches the building but does not know the unit.'
            : `The carrier will not deliver to this address (${verified.deliverability}).`
          : ''
        setAddress({ checked: true, text, warning })
      })
      .catch(() => {
        // Verification is a courtesy here. It must not stand between the user and a letter that
        // every other notice for this loan already goes to the same address.
        if (!cancelled) setAddress({ checked: true, text: '', warning: '' })
      })
    return () => {
      cancelled = true
    }
  }, [loanNumber, status?.configured])

  const onOk = async () => {
    if (!purpose) return
    setSending(true)
    try {
      const res: any = await queueMailPiece({ loanNumber, purpose, documentKey, documentName, overrides })
      if (res?.queued) {
        toast(
          res.reason
            ? `Queued, but held: ${res.reason}. Release it from Servicing > Print & Mail once the address is fixed.`
            : 'Queued for the post. Nothing is spent until the dispatcher submits it.',
          { type: res.reason ? 'warning' : 'success' },
        )
        onQueued && onQueued()
        // `true` is what closes the dialog; `onClose` fires from that, so the parent unmounts.
        return true
      }
      toast(res?.reason || 'It could not be queued.', { type: 'warning' })
    } catch (err: any) {
      toast(err?.message || 'It could not be queued.', { type: 'error' })
    } finally {
      setSending(false)
    }
    return false
  }

  const postage = quote?.postage || {}

  return (
    <Modal
      isOpen
      title="Print and post this document"
      titleOkay={canOffer ? 'Queue it' : ''}
      titleCancel="Cancel"
      loading={sending}
      // Without a provider credential the API refuses to queue at all (410), so the button is off
      // rather than offering an action that can only fail. The quote above still works, which is
      // the half of this dialog that is useful before a tenant has signed up with the provider.
      disabled={!canOffer || !status?.configured || !purpose || quoting}
      onOk={onOk}
      onClose={onClose}
    >
      <div className="w-[560px] max-w-full text-sm space-y-4 mt-1">
        <div>
          <div className="text-xs text-gray-500">Document</div>
          <div className="font-medium break-all">{documentName}</div>
          <div className="text-xs text-gray-500 mt-0.5">
            The exact bytes already filed on loan {loanNumber} are what go in the envelope, not a second render of
            them.
          </div>
        </div>

        <PrintMailStatusBanner status={status} />

        {!canOffer ? (
          <p className="text-amber-800">
            {!config?.enabled
              ? 'Print and mail is switched off for this company.'
              : 'No communication type has been switched on for posting yet.'}{' '}
            An administrator sets this up under Admin Tools &gt; Configuration &gt; Print &amp; Mail.
          </p>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-4">
              <label className="flex flex-col">
                <span className="text-xs text-gray-500 mb-0.5">What this letter is</span>
                <select
                  className="border rounded px-2 py-1.5"
                  value={purpose}
                  onChange={(e) => setPurpose(e.target.value)}
                >
                  {purposes.map((key) => (
                    <option key={key} value={key}>
                      {mailPurposeLabel(key)}
                    </option>
                  ))}
                </select>
                <span className="text-[11px] text-gray-500 mt-0.5">Decides the postage it goes out with.</span>
              </label>

              <label className="flex flex-col">
                <span className="text-xs text-gray-500 mb-0.5">Pages (for the estimate)</span>
                <input
                  className="border rounded px-2 py-1.5"
                  value={pageCount}
                  onChange={(e) => setPageCount(e.target.value.replace(/[^0-9]/g, ''))}
                />
                <span className="text-[11px] text-gray-500 mt-0.5">
                  Only affects the figure below; the letter is posted whole either way.
                </span>
              </label>
            </div>

            <label className="flex flex-col">
              <span className="text-xs text-gray-500 mb-0.5">Extra service</span>
              <select
                className="border rounded px-2 py-1.5"
                value={extraService}
                onChange={(e) => setExtraService(e.target.value)}
              >
                <option value="__default">
                  As configured for this purpose
                  {postage.extraService ? ` (${extraServiceOptions[postage.extraService] || postage.extraService})` : ' (none)'}
                </option>
                <option value="">None</option>
                {Object.entries(extraServiceOptions).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
              <span className="text-[11px] text-gray-500 mt-0.5">
                Certified or registered post for a letter whose delivery may have to be proved later.
              </span>
            </label>

            <div className="rounded border border-gray-200 bg-gray-50 p-3">
              <div className="flex items-baseline justify-between">
                <span className="text-xs text-gray-500">Estimated cost</span>
                <span className="text-lg font-bold text-shade-blue">
                  {quoting ? '...' : mailMoney(quote?.estimatedCostCents)}
                </span>
              </div>
              <p className="text-[11px] text-gray-500 mt-1">
                An estimate from this company&apos;s rate card. The provider returns no price when a letter is
                created, so the only authority on what was charged is their invoice.
              </p>
              {!!quote && (
                <p className="text-[11px] text-gray-600 mt-1">
                  {[
                    postage.mailType === 'usps_standard' ? 'USPS Standard' : 'USPS First Class',
                    postage.color ? 'colour' : 'black and white',
                    postage.doubleSided ? 'double sided' : 'single sided',
                    postage.extraService ? extraServiceOptions[postage.extraService] || postage.extraService : '',
                    postage.returnEnvelope ? 'with a reply envelope' : '',
                    `${quote.pageCount} page${quote.pageCount === 1 ? '' : 's'}`,
                  ]
                    .filter(Boolean)
                    .join(', ')}
                </p>
              )}
            </div>

            {address.checked && (
              <div>
                <div className="text-xs text-gray-500">Addressed to</div>
                {address.text ? <div>{address.text}</div> : <div className="text-gray-400">Not resolved</div>}
                {!!address.warning && <div className="text-amber-800 text-xs mt-0.5">{address.warning}</div>}
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  )
}
